import {
    Column,
    RowData,
    Table,
    TableFeature,
    Updater,
    functionalUpdate,
    makeStateUpdater,
} from '@tanstack/react-table';
import {
    GivebutterColumnFilterOptions,
    GivebutterColumnFilterState,
    GivebutterColumnFiltersTableState,
} from 'types/react-table';

const ColumnFiltersFeature: TableFeature = {
    getInitialState: (state): GivebutterColumnFiltersTableState => ({
        isFilterBarExpanded: false,
        givebutterColumnFilters: {},
        ...state,
    }),

    getDefaultOptions: <TData extends RowData>(
        table: Table<TData>,
    ): GivebutterColumnFilterOptions => ({
        enableGivebutterColumnFilters: false,
        onGivebutterColumnFiltersChange: makeStateUpdater('givebutterColumnFilters', table),
        onCurrentlyEditingColumnChange: makeStateUpdater('currentlyEditingColumn', table),
        onIsFilterBarExpandedChange: makeStateUpdater('isFilterBarExpanded', table),
    }),

    createTable: <TData extends RowData>(table: Table<TData>) => {
        table.setGivebutterColumnFilters = updater => {
            const safeUpdater: Updater<GivebutterColumnFilterState> = old =>
                functionalUpdate(updater, old);
            return table.options.onGivebutterColumnFiltersChange?.(safeUpdater);
        };

        table.updateGivebutterColumnFilters = (value = {}) => {
            table.setGivebutterColumnFilters(old => ({
                ...old,
                ...value,
            }));
        };

        table._updateCurrentlyEditingColumn = value =>
            table.setState(state => ({
                ...state,
                currentlyEditingColumn: value,
            }));

        table.setCurrentlyEditingColumn = updater => {
            const safeUpdater: Updater<Column<any> | undefined> = old =>
                functionalUpdate(updater, old);

            return table.options.onCurrentlyEditingColumnChange?.(safeUpdater);
        };

        table.updateCurrentlyEditingColumn = table.setCurrentlyEditingColumn;

        table.setIsFilterBarExpanded = updater => {
            const safeUpdater: Updater<boolean> = old => functionalUpdate(updater, old);

            return table.options.onIsFilterBarExpandedChange?.(safeUpdater);
        };

        table.updateIsFilterBarExpanded = value => {
            table.setIsFilterBarExpanded(!!value);
        };

        table.setOptions({
            ...table.options,
            mergeOptions: (defaultOptions, options) => {
                return {
                    onCurrentlyEditingColumnChange: v => {
                        table._updateCurrentlyEditingColumn(v as Column<any>);
                    },
                    onIsFilterBarExpandedChange: v => {
                        table.updateIsFilterBarExpanded(v as boolean);
                    },
                    ...defaultOptions,
                    ...options,
                };
            },
        });
    },

    createColumn: <TData extends RowData>(column: Column<TData>, table: Table<TData>) => {
        column.updateGivebutterColumnFilters = (value = []) =>
            table.updateGivebutterColumnFilters({ [column.id]: value });
    },
};

export { ColumnFiltersFeature };
